import { axiosUser } from "./axiosInstance";
import axiosPublic from "./axiosPublic";

/**
 * LK Shop (marketplace) client. Browsing is public and goes through
 * axiosPublic — axiosUser waits up to 3s for a login token before sending,
 * which would stall every catalogue page for a guest. Everything that belongs
 * to the customer (cart, orders, wishlist, returns) needs the token.
 *
 * The backend envelope is { success, message, data, pagination? }; these
 * helpers hand back `data` (or the whole envelope where pagination matters).
 */
const data = (res) => res.data?.data;

/* ------------------------------------------------------------ storefront */

/** The merchandised home: `{ categories, sections, configured }`. `zoneId` targets city-specific sections. */
export const getShopHome = async (zoneId) =>
  data(await axiosPublic.get("/shop/home", { params: zoneId ? { zoneId } : undefined }));

export const getShopCategories = async () => data(await axiosPublic.get("/shop/categories"));

/** `query` is the listing page's own query string — the URL and the API share names. */
export const searchProducts = async (query = "") =>
  data(await axiosPublic.get(`/shop/products${query ? `?${query}` : ""}`));

export const getStore = async (slug, query = "") =>
  data(await axiosPublic.get(`/shop/stores/${encodeURIComponent(slug)}${query ? `?${query}` : ""}`));

export const suggestProducts = async (q, signal) =>
  data(await axiosPublic.get("/shop/suggest", { params: { q }, signal }));

export const getProduct = async (slug, variantId) =>
  data(
    await axiosPublic.get(`/shop/products/${encodeURIComponent(slug)}`, {
      params: variantId ? { variant: variantId } : undefined,
    })
  );

/** Returns the envelope: reviews in `data`, paging in `pagination`. */
export const getProductReviews = async (slug, params = {}) =>
  (await axiosPublic.get(`/shop/products/${encodeURIComponent(slug)}/reviews`, { params })).data;

/* ------------------------------------------------------------------ cart */

export const getCart = async () => data(await axiosUser.get("/shop/cart"));

export const getCartCount = async () => Number(data(await axiosUser.get("/shop/cart/count"))?.count) || 0;

export const addToCart = async (listingId, qty = 1) =>
  data(await axiosUser.post("/shop/cart/items", { listingId, qty }));

export const updateCartItem = async (id, patch) => data(await axiosUser.patch(`/shop/cart/items/${id}`, patch));

export const removeCartItem = async (id) => data(await axiosUser.delete(`/shop/cart/items/${id}`));

/** `{ fromCart: true }` or `{ items: [{ listingId, qty }] }` (buy now). */
export const previewCheckout = async (payload) => data(await axiosUser.post("/shop/checkout/preview", payload));

/* ---------------------------------------------------------------- orders */

export const placeShopOrder = async ({ idempotencyKey, input }) =>
  data(await axiosUser.post("/commerce/orders", { vertical: "shop", idempotencyKey, input }));

export const verifyShopPayment = async (orderId, payload) =>
  data(await axiosUser.post(`/commerce/orders/${orderId}/verify-payment`, payload));

/** Local only: the backend's fake gateway "pays" an order so checkout can be exercised without Razorpay. */
export const sandboxPay = async (gatewayOrderId) =>
  (await axiosPublic.post(`/commerce/sandbox/pay/${encodeURIComponent(gatewayOrderId)}`)).data;

export const getShopOrders = async (page = 1) => (await axiosUser.get("/shop/orders", { params: { page } })).data;

export const getOrder = async (id) => data(await axiosUser.get(`/commerce/orders/${id}`));

/** Kuicko rider for one shipment: `{ delivery, live: { status, rider: {lat,lng,heading}|null, etaMinutes, pickup, drop } | null }`. */
export const getShipmentTracking = async (orderId, shipmentId) =>
  data(await axiosUser.get(`/shop/orders/${orderId}/shipments/${shipmentId}/tracking`));

/** A fresh checkout for an order still at pending_payment (409 NOT_PAYABLE once it has expired). */
/** Release an order still at pending_payment (no input: the whole order, stock goes back). */
export const cancelUnpaidOrder = async (id) => (await axiosUser.post(`/commerce/orders/${id}/cancel`, {})).data;

export const getOrderCheckout = async (id) => data(await axiosUser.get(`/commerce/orders/${id}/checkout`));

export const cancelOrderItems = async (id, { idempotencyKey, items }) =>
  (await axiosUser.post(`/commerce/orders/${id}/cancel`, { idempotencyKey, input: items ? { items } : {} })).data;

/* ---------------------------------------------------- returns & reviews */

export const getReturns = async () => data(await axiosUser.get("/shop/returns"));

export const requestReturn = async (payload) => data(await axiosUser.post("/shop/returns", payload));

export const cancelReturn = async (id) => data(await axiosUser.post(`/shop/returns/${id}/cancel`));

export const submitReview = async (payload) => (await axiosUser.post("/shop/reviews", payload)).data;

/* -------------------------------------------------------------- wishlist */

export const getWishlist = async () => data(await axiosUser.get("/shop/wishlist"));

export const addToWishlist = async (productId, variantId) =>
  data(await axiosUser.post("/shop/wishlist", { productId, variantId }));

export const removeFromWishlist = async (productId) => data(await axiosUser.delete(`/shop/wishlist/${productId}`));

/* ------------------------------------------------------------- addresses */

export const getAddresses = async () => data(await axiosUser.get("/addresses")) || [];

export const createAddress = async (payload) => data(await axiosUser.post("/addresses", payload));

export const updateAddress = async (id, payload) => data(await axiosUser.put(`/addresses/${id}`, payload));

export const deleteAddress = async (id) => (await axiosUser.delete(`/addresses/${id}`)).data;
